import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  EmptyTableState,
  Loading,
  toast,
  useApiClient,
  useRequireOrganization,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { GripVertical, Map, Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { TourMultiSelectDialog } from '@/components/tours/TourMultiSelectDialog';
import type { Tour } from '@/config/objects/tours';
import {
  type TopPickTour,
  useHotelTopPicks,
  useUpdateHotelTopPicks,
} from '@/hooks/useHotelTopPicks';

interface HotelTopPicksListProps {
  hotelId: string;
  className?: string;
}

interface SortablePickItemProps {
  tour: TopPickTour;
  position: number;
  onRemove: (tourId: string) => void;
  /** When true the image + name link to the tour detail page (internal only). */
  linkable: boolean;
}

/**
 * A single sortable Top Pick row with a drag handle, position badge, and
 * remove button. The image + name navigate to the tour when `linkable`.
 */
function SortablePickItem({ tour, position, onRemove, linkable }: SortablePickItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: tour.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const thumb = tour.featuredImage?.url ? (
    <img
      src={tour.featuredImage.url}
      alt={tour.name}
      className="h-10 w-14 shrink-0 rounded-md object-cover"
    />
  ) : (
    <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded-md bg-muted">
      <Map className="h-4 w-4 text-muted-foreground" />
    </div>
  );

  const details = (
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-medium">{tour.name}</div>
      {tour.tourType?.name && (
        <div className="truncate text-xs text-muted-foreground">{tour.tourType.name}</div>
      )}
    </div>
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2 shadow-sm"
    >
      <button
        type="button"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-5 w-5" />
      </button>

      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-600">
        <span className="text-sm font-semibold">{position + 1}</span>
      </div>

      {linkable ? (
        <Link
          to={`/tours/${tour.id}`}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-md transition-colors hover:text-primary"
        >
          {thumb}
          {details}
        </Link>
      ) : (
        <>
          {thumb}
          {details}
        </>
      )}

      <Button
        variant="ghost"
        size="sm"
        aria-label={`Remove ${tour.name} from Top Picks`}
        onClick={() => onRemove(tour.id)}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * Drag-and-drop curated list of Top Picks tours for a hotel. The mobile app
 * surfaces this list in the Top Picks tab, in the order shown here.
 */
export function HotelTopPicksList({ hotelId, className }: HotelTopPicksListProps) {
  const apiClient = useApiClient();
  // Only internal users can open the (admin) tour detail page; hotel users get
  // non-clickable rows until a hotel-safe tour preview exists.
  const { isInternal } = useRequireOrganization();
  const { data, isLoading } = useHotelTopPicks(hotelId);
  const updateMutation = useUpdateHotelTopPicks(hotelId);
  const [addOpen, setAddOpen] = useState(false);

  const tours = useMemo(() => data?.data ?? [], [data?.data]);
  const max = data?.meta?.max ?? 10;
  const pinnedIds = useMemo(() => new Set(tours.map((t) => t.id)), [tours]);
  const isAtCap = tours.length >= max;
  const remainingSlots = Math.max(0, max - tours.length);

  // Candidate pool: tours linked to this hotel (server-scoped via the
  // `/hotels/:id/tours` endpoint), minus the ones already pinned.
  const { data: candidateData, isLoading: candidatesLoading } = useQuery<{
    data: Tour[];
  }>({
    queryKey: ['HotelTours', hotelId, 'all'],
    enabled: addOpen,
    queryFn: () =>
      apiClient.get<{ data: Tour[] }>(`/hotels/${hotelId}/tours?limit=100`),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tours.findIndex((t) => t.id === active.id);
    const newIndex = tours.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(tours, oldIndex, newIndex);
    updateMutation.mutate({
      tourIds: reordered.map((t) => t.id),
      optimisticTours: reordered.map((t, i) => ({ ...t, topPickOrder: i })),
    });
  };

  const handleRemove = (tourId: string) => {
    const next = tours.filter((t) => t.id !== tourId);
    updateMutation.mutate({
      tourIds: next.map((t) => t.id),
      optimisticTours: next.map((t, i) => ({ ...t, topPickOrder: i })),
    });
  };

  const handleAddMany = async (selected: Tour[]) => {
    if (selected.length === 0) return;
    if (selected.length > remainingSlots) {
      toast.error(`Top Picks is capped at ${max} tours`);
      return;
    }
    const next: TopPickTour[] = [
      ...tours,
      ...selected.map((tour) => tour as TopPickTour),
    ];
    await updateMutation.mutateAsync({
      tourIds: next.map((t) => t.id),
      optimisticTours: next.map((t, i) => ({ ...t, topPickOrder: i })),
    });
    toast.success(
      `Added ${selected.length} ${selected.length === 1 ? 'tour' : 'tours'} to Top Picks`,
    );
    setAddOpen(false);
  };

  return (
    <div className={`flex flex-col rounded-xl border bg-card shadow-sm ${className ?? ''}`}>
      <div className="border-b px-6 py-4">
        <h2 className="text-lg font-medium tracking-tight text-zinc-900">Top Picks</h2>
        <p className="text-sm text-muted-foreground">
          Featured tours shown to mobile users in the Top Picks tab. Drag to
          reorder. Up to {max} tours.
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-6">
        {isLoading && <Loading size="sm" />}

        {!isLoading && tours.length === 0 && (
          <EmptyTableState
            icon={<Map className="h-6 w-6" />}
            title="No Top Picks yet"
            description={`Add up to ${max} tours that mobile users should see first.`}
            action={{ label: 'Add tours', onClick: () => setAddOpen(true), icon: Plus }}
          />
        )}

        {!isLoading && tours.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={tours.map((t) => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {tours.map((tour, index) => (
                  <SortablePickItem
                    key={tour.id}
                    tour={tour}
                    position={index}
                    onRemove={handleRemove}
                    linkable={isInternal}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Dashed add-tours affordance that grows to fill the card's extra
            height (the card is height-matched to the Loop Tours card), with the
            primary Add tours button centered inside. */}
        {!isLoading && tours.length > 0 && !isAtCap && (
          <div className="flex min-h-[88px] flex-1 items-center justify-center rounded-lg border border-dashed">
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" />
              Add tours
            </Button>
          </div>
        )}
      </div>

      <TourMultiSelectDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add tours to Top Picks"
        description="Pick tours already linked to this hotel. New picks land at the bottom of the list — drag to reorder afterwards."
        tours={candidateData?.data ?? []}
        isLoading={candidatesLoading}
        excludeIds={pinnedIds}
        maxSelection={remainingSlots}
        onSubmit={handleAddMany}
        emptyMessage="Every linked tour is already a Top Pick — link more tours in the All Tours section to add them here."
      />
    </div>
  );
}
